<template>
  <ClientOnly>
    <section
      v-if="campusBlock?.Campuses?.length"
      class="campuses-map-section">
      <div class="container">
        <h2 class="campuses-map__title">
          {{ campusBlock.Title || 'Nos campus' }}
        </h2>

        <!-- ── CARTE ────────────────────────────────── -->
        <div class="campuses-map__map-wrapper">
          <div
            ref="mapEl"
            class="campuses-map__map" />

          <div
            v-if="isGeocoding"
            class="campuses-map__loader">
            <span>Localisation des campus…</span>
          </div>
        </div>

        <!-- ── LISTE DES CAMPUS VISIBLES ───────────── -->
        <div class="campuses-map__list">
          <div class="campuses-map__list-header">
            <span class="campuses-map__list-count">
              {{ visibleCampuses.length }} / {{ locatedCampuses.length }} campus visibles
            </span>
          </div>

          <ul
            v-if="visibleCampuses.length"
            class="campuses-map__items">
            <li
              v-for="item in visibleCampuses"
              :key="item.id"
              :data-campus-id="item.id"
              class="campuses-map__item"
              :class="{ 'campuses-map__item--highlight': highlightedId === item.id }"
              @mouseenter="highlightedId = item.id"
              @mouseleave="highlightedId = null"
              @click="flyToCampus(item.id)">
              <span class="campuses-map__item-name">{{ item.Name }}</span>
              <span
                v-if="item.Address.ZipCode || item.Address.City"
                class="campuses-map__item-address">
                {{ item.Address.Address }}
                <template v-if="item.Address.Address">— </template>
                {{ item.Address.ZipCode }} {{ item.Address.City }}
              </span>
            </li>
          </ul>

          <p
            v-else
            class="campuses-map__empty">
            Aucun campus dans la zone visible. Dézoome pour en voir plus.
          </p>

          <div
            v-if="unlocatedCampuses.length"
            class="campuses-map__unlocated">
            <span class="campuses-map__unlocated-title"> Non localisés sur la carte : </span>
            <ul class="campuses-map__unlocated-list">
              <li
                v-for="item in unlocatedCampuses"
                :key="item.id">
                — {{ item.Name }}
                <template v-if="item.Address.City">
                  — {{ item.Address.ZipCode }} {{ item.Address.City }}
                </template>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import type { Map as LeafletMap, Marker as LeafletMarker, MarkerClusterGroup } from 'leaflet'
import type { CampusBlock, CampusObj } from '~/types/school'
import { configureLeafletDefaultIcons } from '~/utils/leafletIcons'
// getAddressCoordinates est auto-importé depuis app/utils/geocoding.ts

// ── Types locaux ───────────────────────────────────────────
interface LocatedCampus extends CampusObj {
  id: number
  lat: number
  lng: number
}

interface UnlocatedCampus extends CampusObj {
  id: number
}

// ── Props ──────────────────────────────────────────────────
const props = defineProps<{
  campusBlock: CampusBlock | null
}>()

// ── State ──────────────────────────────────────────────────
const mapEl = ref<HTMLElement | null>(null)
// shallowRef : Leaflet gère ses propres references circulaires
const map = shallowRef<LeafletMap | null>(null)
const markerCluster = shallowRef<MarkerClusterGroup | null>(null)
const markers = shallowRef<Map<number, LeafletMarker>>(new Map())

const locatedCampuses = ref<LocatedCampus[]>([])
const unlocatedCampuses = ref<UnlocatedCampus[]>([])
const visibleIds = ref<Set<number>>(new Set())
const highlightedId = ref<number | null>(null)
const isGeocoding = ref(false)

// ── Computed ───────────────────────────────────────────────
const visibleCampuses = computed<LocatedCampus[]>(() =>
  locatedCampuses.value.filter((c) => visibleIds.value.has(c.id)),
)

// ── Résolution des coordonnées ─────────────────────────────
async function resolveCoordinates(): Promise<void> {
  const campuses = props.campusBlock?.Campuses ?? []
  if (!campuses.length) {
    locatedCampuses.value = []
    unlocatedCampuses.value = []
    return
  }

  isGeocoding.value = true
  const located: LocatedCampus[] = []
  const unlocated: UnlocatedCampus[] = []

  await Promise.all(
    campuses.map(async (campus, index) => {
      try {
        const coords = await getAddressCoordinates(campus.Address)
        if (coords) {
          located.push({ ...campus, id: index, lat: coords.lat, lng: coords.lng })
        } else {
          unlocated.push({ ...campus, id: index })
        }
      } catch {
        unlocated.push({ ...campus, id: index })
      }
    }),
  )

  locatedCampuses.value = located
  unlocatedCampuses.value = unlocated
  isGeocoding.value = false
}

// ── Init Leaflet ───────────────────────────────────────────
async function initMap(): Promise<void> {
  if (!mapEl.value) return

  await loadLeafletStyles()
  const L = (await import('leaflet')).default
  await import('leaflet.markercluster')

  configureLeafletDefaultIcons(L)

  map.value = L.map(mapEl.value, {
    scrollWheelZoom: true,
    zoomControl: true,
  }).setView([46.603354, 1.888334], 6) // Centre France par défaut

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19,
  }).addTo(map.value)

  markerCluster.value = L.markerClusterGroup({
    showCoverageOnHover: false,
    spiderfyOnMaxZoom: true,
    maxClusterRadius: 50,
  })
  map.value.addLayer(markerCluster.value)

  // Re-sync le highlight quand les clusters changent (zoom, regroupement)
  markerCluster.value.on('animationend', () => {
    if (highlightedId.value != null) applyHighlight(highlightedId.value)
  })

  map.value.on('moveend', updateVisibleIds)
  map.value.on('zoomend', updateVisibleIds)

  // ⚡ Force le recalcul des tuiles quand le conteneur prend ses vraies dimensions
  // (nécessaire avec ClientOnly + transitions + CSS chargé en async)
  setTimeout(() => {
    map.value?.invalidateSize()
  }, 100)
}

// ── Rendu des markers + fit bounds ─────────────────────────
async function renderMarkers(): Promise<void> {
  if (!map.value || !markerCluster.value) return

  const L = (await import('leaflet')).default

  markerCluster.value.clearLayers()
  markers.value = new Map()

  if (!locatedCampuses.value.length) {
    updateVisibleIds()
    return
  }

  const layers: LeafletMarker[] = []

  for (const c of locatedCampuses.value) {
    const marker = L.marker([c.lat, c.lng])

    // ⚠️ interactive: false sur le tooltip pour éviter qu'il ne gêne le hover,
    // et on le gère entièrement via highlightedId (pas de hover natif)
    marker.bindTooltip(c.Name, {
      direction: 'top',
      offset: [0, -8],
      permanent: false,
    })

    marker.on('mouseover', () => {
      highlightedId.value = c.id
    })
    marker.on('mouseout', () => {
      if (highlightedId.value === c.id) highlightedId.value = null
    })

    marker.on('click', () => {
      highlightedId.value = c.id
      scrollItemIntoView(c.id)
    })

    markers.value.set(c.id, marker)
    layers.push(marker)
  }

  markerCluster.value.addLayers(layers)

  const bounds = L.latLngBounds(locatedCampuses.value.map((c) => [c.lat, c.lng]))
  map.value.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })

  updateVisibleIds()
}

// ── Calcul des IDs visibles dans la viewport ───────────────
function updateVisibleIds(): void {
  if (!map.value) return
  const bounds = map.value.getBounds()
  const next = new Set<number>()
  for (const c of locatedCampuses.value) {
    if (bounds.contains([c.lat, c.lng])) next.add(c.id)
  }
  visibleIds.value = next
}

// ── Fly to + scroll dans la liste ─────────────────────────
function flyToCampus(id: number): void {
  const campus = locatedCampuses.value.find((c) => c.id === id)
  if (!campus || !map.value) return

  map.value.flyTo([campus.lat, campus.lng], 15, { duration: 0.8 })
  highlightedId.value = id
}

function scrollItemIntoView(id: number): void {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>(`[data-campus-id="${id}"]`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  })
}

// ── Highlight sur la carte via survol liste ────────────────
// Si le marker est dans un cluster, on highlight le cluster parent.
function clearHighlight(id: number): void {
  const marker = markers.value.get(id)
  if (!marker || !markerCluster.value) return

  const parent = markerCluster.value.getVisibleParent(marker)

  // Cas 1 : le marker est visible individuellement
  if (parent === marker) {
    marker.setZIndexOffset(0)
    marker.closeTooltip()
    return
  }

  // Cas 2 : le marker est dans un cluster → on retire la classe du DOM du cluster
  const el = parent?.getElement?.() as HTMLElement | undefined
  el?.classList.remove('marker-cluster--highlight')
}

function applyHighlight(id: number): void {
  const marker = markers.value.get(id)
  if (!marker || !markerCluster.value) return

  const parent = markerCluster.value.getVisibleParent(marker)

  // Cas 1 : marker visible
  if (parent === marker) {
    marker.setZIndexOffset(1000)
    marker.openTooltip()
    return
  }

  // Cas 2 : dans un cluster → on highlight le DOM du cluster
  const el = parent?.getElement?.() as HTMLElement | undefined
  el?.classList.add('marker-cluster--highlight')
}

watch(highlightedId, (id, prevId) => {
  if (prevId != null) clearHighlight(prevId)
  if (id != null) applyHighlight(id)
})

// ── Lifecycle ──────────────────────────────────────────────
onMounted(() => {
  // <ClientOnly> monte son contenu APRÈS le onMounted du parent.
  // On attend donc que le ref soit disponible via un watch one-shot.
  const stop = watch(
    mapEl,
    async (el) => {
      if (!el) return
      stop()
      await initMap()
      await resolveCoordinates()
      await renderMarkers()
    },
    { immediate: true, flush: 'post' },
  )
})

watch(
  () => props.campusBlock,
  async () => {
    await resolveCoordinates()
    await renderMarkers()
  },
  { deep: true },
)

onBeforeUnmount(() => {
  if (map.value) {
    map.value.off()
    map.value.remove()
    map.value = null
  }
})
</script>

<style scoped>
.campuses-map-section {
  padding: var(--space-16) var(--container-pad);
  background: linear-gradient(135deg, rgba(55, 65, 85, 0.4) 0%, rgba(30, 40, 55, 0.4) 100%);
  border-radius: var(--radius-2xl);
  margin: var(--space-12) var(--container-pad);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.campuses-map__title {
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-12);
  text-align: left;
}

.campuses-map__map-wrapper {
  position: relative;
  z-index: 0;
  isolation: isolate;
  width: 100%;
  height: 480px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.2);
  margin-bottom: var(--space-6);
}

.campuses-map__map-wrapper :deep(.leaflet-container) {
  z-index: 0;
}

.campuses-map__map {
  width: 100%;
  height: 100%;
}

.campuses-map__loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  color: rgba(255, 255, 255, 0.9);
  font-size: var(--text-sm);
  font-weight: 500;
  z-index: 500;
  pointer-events: none;
}

.campuses-map__list {
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: var(--radius-lg);
  padding: var(--space-4) var(--space-6);
}

.campuses-map__list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-3);
  padding-bottom: var(--space-3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.campuses-map__list-count {
  font-size: var(--text-xs);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
}

.campuses-map__items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  max-height: 280px;
  overflow-y: auto;
}

.campuses-map__item {
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  transition: background-color var(--duration-fast) var(--ease-out);
  cursor: pointer;
}

.campuses-map__item:hover,
.campuses-map__item--highlight {
  background-color: rgba(255, 255, 255, 0.08);
}

.campuses-map__item-name {
  font-size: var(--text-sm);
  font-weight: 700;
  color: white;
}

.campuses-map__item-address {
  font-size: var(--text-xs);
  color: rgba(255, 255, 255, 0.7);
}

.campuses-map__empty {
  font-size: var(--text-sm);
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  text-align: center;
  padding: var(--space-6);
  margin: 0;
}

.campuses-map__unlocated {
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.campuses-map__unlocated-title {
  display: block;
  font-size: var(--text-xs);
  font-weight: 600;
  color: rgba(255, 180, 100, 0.9);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  margin-bottom: var(--space-2);
}

.campuses-map__unlocated-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.campuses-map__unlocated-list li {
  font-size: var(--text-xs);
  color: rgba(255, 255, 255, 0.6);
  padding-left: var(--space-3);
}

:deep(.leaflet-control-attribution) {
  background-color: rgba(255, 255, 255, 0.8) !important;
  font-size: 11px !important;
}

/* prettier-ignore */
:deep(.marker-cluster) {
  background-color: rgba(3, 160, 241, 0.2) !important;
  transition: transform 0.2s ease-out, box-shadow 0.2s ease-out;
}

:deep(.marker-cluster div) {
  background-color: rgba(3, 160, 241, 0.7) !important;
  color: white !important;
  font-weight: 700 !important;
}

:deep(.marker-cluster--highlight) {
  transform: scale(1.15);
  z-index: 10 !important;
}

/* prettier-ignore */
:deep(.marker-cluster--highlight div) {
  background-color: rgba(3, 160, 241, 1) !important;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.9), 0 0 20px rgba(3, 160, 241, 0.8);
}

@media (max-width: 768px) {
  .campuses-map-section {
    padding: var(--space-12) var(--container-pad);
    margin: var(--space-8) var(--container-pad);
  }

  .campuses-map__title {
    font-size: var(--text-xl);
    margin-bottom: var(--space-8);
  }

  .campuses-map__map-wrapper {
    height: 360px;
  }

  .campuses-map__list {
    padding: var(--space-3) var(--space-4);
  }
}

@media (max-width: 640px) {
  .campuses-map-section {
    padding: var(--space-8) var(--container-pad);
    margin: var(--space-6) 0;
    border-radius: var(--radius-xl);
  }

  .campuses-map__title {
    font-size: var(--text-lg);
    margin-bottom: var(--space-6);
  }

  .campuses-map__map-wrapper {
    height: 300px;
  }

  .campuses-map__items {
    max-height: 220px;
  }
}
</style>
